import React, {
  useState,
  useEffect,
  useRef,
  memo,
  useLayoutEffect,
} from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { createTransitionOverlay } from '@/utils/transitionOverlay';
import { ProjectsData } from '@/data/projects-data';
import { Language } from '@/types';
import { useAppContext } from '@/context/AppContext';
import styles from './Projects.module.scss';
import ImageComponent from '@/components/imageComponent/ImageComponent';
import Footer from '@/components/footer/Footer';

const getItemsPerRow = () => {
  if (window.innerWidth <= 480) return 1;
  return 2;
};

const Projects: React.FC = () => {
  const { language } = useAppContext();
  const navigate = useNavigate();
  const [visibleProjects, setVisibleProjects] = useState(6);
  const projectGridRef = useRef<HTMLDivElement>(null);
  const projectsSectionRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const [headerRevealed, setHeaderRevealed] = useState(false);
  const [gridRevealed, setGridRevealed] = useState(false);
  const [revealedItems, setRevealedItems] = useState<Set<number>>(new Set());
  const [initialRevealed, setInitialRevealed] = useState(false);

  useLayoutEffect(() => {
    const onResize = () => {
      setVisibleProjects((prev) => prev);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target as HTMLElement;
          if (!entry.isIntersecting || entry.intersectionRatio <= 0.3) return;
          const type = el.getAttribute('data-reveal');
          if (type === 'header') setHeaderRevealed(true);
          if (type === 'grid') setGridRevealed(true);
          if (type === 'item') {
            const idxStr = el.getAttribute('data-index');
            const idx = idxStr ? parseInt(idxStr, 10) : -1;
            if (idx >= 0 && !revealedItems.has(idx)) {
              const itemsPerRow = getItemsPerRow();
              const isMobile = itemsPerRow === 1;

              if (isMobile) {
                setRevealedItems((prev) => new Set([...prev, idx]));
              } else {
                const rowIndex = Math.floor(idx / itemsPerRow);
                const start = rowIndex * itemsPerRow;
                const end = Math.min(start + itemsPerRow, visibleProjects);
                setRevealedItems((prev) => {
                  const next = new Set(prev);
                  for (let i = start; i < end; i++) {
                    if (i < ProjectsData.length) next.add(i);
                  }
                  return next;
                });
              }
            }
          }
        });
      },
      { threshold: 0.3, rootMargin: '0px 0px -10% 0px' },
    );

    const headerEl = headerRef.current;
    const gridEl = projectGridRef.current;
    if (headerEl) observer.observe(headerEl);
    if (gridEl) observer.observe(gridEl);

    const itemNodes = projectGridRef.current?.querySelectorAll(
      '[data-reveal="item"]',
    );
    itemNodes?.forEach((node) => observer.observe(node));

    return () => observer.disconnect();
  }, [visibleProjects, revealedItems]);

  const handleLoadMore = () => {
    setVisibleProjects((prev) => Math.min(prev + 6, ProjectsData.length));
  };

  const handleProjectClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string | number,
    brandRgb: string,
  ) => {
    e.preventDefault();
    try {
      if (window.location.pathname === '/') {
        window.history.replaceState(null, '', '/#projects');
      }
    } catch {}
    const overlay = createTransitionOverlay(brandRgb, 120);
    overlay.style.opacity = '1';
    setTimeout(() => navigate(`/project/${id}`), 120);
  };

  return (
    <div className={styles.projects} id="projects" ref={projectsSectionRef}>
      <div
        className={`${styles.header} ${headerRevealed ? styles.revealed : ''}`}
        ref={headerRef}
        data-reveal="header"
      >
        <h1>{language === Language.EN ? 'Projects' : 'Projetos'}</h1>
      </div>
      <div className={styles.projectContainer}>
        <div
          className={`${styles.projectGrid} ${gridRevealed ? styles.revealed : ''}`}
          ref={projectGridRef}
          data-reveal="grid"
        >
          {ProjectsData.slice(0, visibleProjects).map((item, index) => (
            <Link
              key={item.id}
              to={`/project/${item.id}`}
              className={`${styles.projectItem} ${revealedItems.has(index) ? styles.visible : ''} ${item.id === 1 ? styles.movieDB : ''} ${item.id === 4 ? styles.procheck : ''}`}
              aria-label={item.name}
              title={item.name}
              data-reveal="item"
              data-index={index}
              style={{
                ['--brand-rgb' as any]: item.backgroundColor || '255,255,255',
                ['--reveal-delay' as any]: `${(index % getItemsPerRow()) * 70}ms`,
              }}
              onClick={(e) =>
                handleProjectClick(e, item.id, item.backgroundColor)
              }
            >
              <div className={styles.projectTitle}>
                <ImageComponent
                  name={item.name}
                  img={item.logo}
                  desc={item.name}
                />
              </div>
            </Link>
          ))}
        </div>
        {visibleProjects < ProjectsData.length && (
          <div className={styles.loadMoreContainer}>
            <button className={styles.loadMoreButton} onClick={handleLoadMore}>
              {language === Language.EN ? 'Load More' : 'Carregar Mais'}
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
};

Projects.displayName = 'Projects';
export default memo(Projects);
